import React, { useCallback, useEffect, useState } from 'react';
import { Table, Button, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import { Undo2, FileText, PlayCircle } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import VerificationDetailsModal from '../Verification/VerificationDetailsModal';
import {
  RUN_STATUS_META,
  formatMonthYear,
  formatNumber,
  round2,
  unwrapData,
} from './assetUtils';

/** The current and the three previous periods as 'YYYY-MM' (UTC). */
function recentPeriods() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 4; i += 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

/**
 * RunHistory — table over asset_depreciation_runs: period, status, belopp,
 * antal tillgångar, verifikat link (shared VerificationDetailsModal), undo on
 * the latest posted run (Fortnox undo semantics — only the newest run can be
 * rolled back) and the plain-language error text on failed runs.
 */
const RunHistory = ({ companyId }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyRunId, setBusyRunId] = useState(null);
  const [verificationRow, setVerificationRow] = useState(null);
  const [showVerificationDetails, setShowVerificationDetails] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [runPeriod, setRunPeriod] = useState(recentPeriods()[0]);
  const [runPreview, setRunPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(getApiUrl(`${companyId}/assets/runs`), { withCredentials: true });
      const data = unwrapData(response);
      const list = Array.isArray(data) ? data : data?.runs || [];
      // Newest first.
      list.sort((a, b) => String(b.period).localeCompare(String(a.period)));
      setRuns(list);
    } catch (err) {
      setError('Det gick inte att hämta avskrivningskörningarna. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const latestPostedRunId = runs.find((run) => run.status === 'posted')?.run_id;

  const handleShowVerification = async (run) => {
    setBusyRunId(run.run_id);
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/verification/${run.verification_id}`),
        { withCredentials: true }
      );
      const verification = response.data;
      let debitSum = 0;
      let creditSum = 0;
      (verification.transactions || []).forEach((t) => {
        const amount = parseFloat(t.amount) || 0;
        if (t.transaction_type === 'debit') debitSum += amount;
        else creditSum += amount;
      });
      setVerificationRow({ ...verification, debitSum: round2(debitSum), creditSum: round2(creditSum) });
      setShowVerificationDetails(true);
    } catch (err) {
      toast.error('Kunde inte hämta verifikatet.');
    } finally {
      setBusyRunId(null);
    }
  };

  const openRunModal = () => {
    setRunPeriod(recentPeriods()[0]);
    setRunPreview(null);
    setShowRunModal(true);
  };

  const fetchPreview = useCallback(async (period) => {
    setPreviewLoading(true);
    setRunPreview(null);
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/assets/runs`),
        { period, preview: true },
        { withCredentials: true }
      );
      setRunPreview(unwrapData(response));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte hämta förhandsgranskningen.');
    } finally {
      setPreviewLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (showRunModal && runPeriod) fetchPreview(runPeriod);
  }, [showRunModal, runPeriod, fetchPreview]);

  const existingStatus = runPreview?.existingRun?.status || null;

  const handleExecuteRun = async () => {
    setPosting(true);
    try {
      await axios.post(
        getApiUrl(`${companyId}/assets/runs`),
        { period: runPeriod },
        { withCredentials: true }
      );
      toast.success(`Avskrivningarna för ${formatMonthYear(runPeriod, 'long')} är bokförda.`);
      setShowRunModal(false);
      await fetchRuns();
    } catch (err) {
      if (err.response?.status === 409) {
        // Already posted, or another run for the period is in progress.
        toast.info(err.response?.data?.error || 'Perioden är redan bokförd.');
        setShowRunModal(false);
        await fetchRuns();
      } else {
        toast.error(err.response?.data?.error || 'Kunde inte bokföra avskrivningarna.');
      }
    } finally {
      setPosting(false);
    }
  };

  const runModal = (
    <Modal show={showRunModal} onHide={() => setShowRunModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h6" className="d-flex align-items-center gap-2">
          <PlayCircle size={18} style={{ color: 'var(--primary)' }} /> Bokför avskrivningar
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Period</Form.Label>
          <Form.Select value={runPeriod} onChange={(e) => setRunPeriod(e.target.value)}>
            {recentPeriods().map((p) => (
              <option key={p} value={p}>{formatMonthYear(p, 'long')}</option>
            ))}
          </Form.Select>
        </Form.Group>
        {previewLoading && (
          <div className="d-flex justify-content-center py-4"><Spinner animation="border" size="sm" /></div>
        )}
        {!previewLoading && runPreview && existingStatus === 'posted' && (
          <Alert variant="info" className="py-2" style={{ fontSize: '0.875rem' }}>
            Den här perioden är redan bokförd.
          </Alert>
        )}
        {/* A failed or skipped run can be run again; its stored reason says what stopped it. */}
        {!previewLoading && runPreview && (existingStatus === 'failed' || existingStatus === 'skipped') && (
          <Alert variant="warning" className="py-2" style={{ fontSize: '0.875rem' }}>
            {existingStatus === 'failed'
              ? 'Den tidigare körningen för perioden misslyckades.'
              : 'Den tidigare körningen för perioden hoppades över.'}
            {runPreview.existingRun.error_log && <div style={{ marginTop: 4 }}>{runPreview.existingRun.error_log}</div>}
          </Alert>
        )}
        {!previewLoading && runPreview && existingStatus === 'pending' && (
          <Alert variant="info" className="py-2" style={{ fontSize: '0.875rem' }}>
            En körning för perioden pågår. Om den har avbrutits kan du bokföra perioden igen om en stund.
          </Alert>
        )}
        {!previewLoading && runPreview && existingStatus !== 'posted' && (
          runPreview.lines?.length > 0 ? (
            <>
              <Table size="sm" className="asset-run-table mb-2">
                <thead>
                  <tr><th>Tillgång</th><th className="text-end">Belopp</th></tr>
                </thead>
                <tbody>
                  {runPreview.lines.map((line) => (
                    <tr key={line.asset_id}>
                      <td>{line.name} <span className="text-muted">#{line.asset_number}</span></td>
                      <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumber(line.amount)} kr
                      </td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700 }}>
                    <td>Totalt ({runPreview.assetCount} tillgångar)</td>
                    <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumber(runPreview.totalAmount)} kr
                    </td>
                  </tr>
                </tbody>
              </Table>
              <p className="text-muted mb-0" style={{ fontSize: '0.8125rem' }}>
                Ett verifikat (serie A) bokförs {runPreview.postingDate}.
              </p>
            </>
          ) : (
            <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
              Inga tillgångar att skriva av denna period.
            </p>
          )
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setShowRunModal(false)}>Avbryt</Button>
        <Button
          variant="primary"
          disabled={posting || previewLoading || !runPreview || existingStatus === 'posted' || !(runPreview.lines?.length > 0)}
          onClick={handleExecuteRun}
        >
          {posting ? 'Bokför…' : 'Bokför'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const handleUndo = async (run) => {
    const result = await Swal.fire({
      title: 'Ångra körningen?',
      text: `Avskrivningarna för ${formatMonthYear(run.period, 'long')} tas bort och verifikatet annulleras.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Ja, ångra',
      cancelButtonText: 'Avbryt',
    });
    if (!result.isConfirmed) return;

    setBusyRunId(run.run_id);
    try {
      await axios.post(getApiUrl(`${companyId}/assets/runs/${run.run_id}/undo`), {}, { withCredentials: true });
      toast.success(`Körningen för ${formatMonthYear(run.period, 'long')} är ångrad.`);
      await fetchRuns();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte ångra körningen.');
    } finally {
      setBusyRunId(null);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error) return <Alert variant="danger">{error}</Alert>;

  if (runs.length === 0) {
    return (
      <>
        <div className="text-center text-muted py-5">
          <p className="mb-1" style={{ fontWeight: 600 }}>Inga avskrivningskörningar ännu.</p>
          <p className="mb-3" style={{ fontSize: '0.875rem' }}>
            När månaden är slut bokför LedgerLoop avskrivningarna automatiskt — de dyker upp här.
          </p>
          <Button variant="primary" className="d-inline-flex align-items-center gap-2" onClick={openRunModal}>
            <PlayCircle size={16} /> Bokför avskrivningar nu
          </Button>
        </div>
        {runModal}
      </>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        <Button
          variant="outline-primary"
          size="sm"
          className="d-inline-flex align-items-center gap-2"
          onClick={openRunModal}
        >
          <PlayCircle size={15} /> Bokför avskrivningar
        </Button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table hover responsive className="align-middle asset-run-table">
          <thead>
            <tr>
              <th>Period</th>
              <th>Status</th>
              <th className="text-end">Belopp</th>
              <th className="text-end">Tillgångar</th>
              <th>Verifikat</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => {
              const meta = RUN_STATUS_META[run.status] || RUN_STATUS_META.pending;
              return (
                <tr key={run.run_id}>
                  <td style={{ fontWeight: 600 }}>{formatMonthYear(run.period, 'long')}</td>
                  <td>
                    <span
                      style={{
                        background: meta.bg,
                        color: meta.color,
                        borderRadius: 999,
                        padding: '2px 10px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {meta.label}
                    </span>
                    {(run.status === 'failed' || run.status === 'skipped') && run.error_log && (
                      <div style={{ color: run.status === 'failed' ? '#b91c1c' : '#6b7280', fontSize: '0.75rem', marginTop: 4, maxWidth: 320 }}>
                        {run.error_log}
                      </div>
                    )}
                  </td>
                  <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {run.total_amount !== null && run.total_amount !== undefined
                      ? `${formatNumber(run.total_amount)} kr`
                      : '—'}
                  </td>
                  <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{run.asset_count ?? '—'}</td>
                  <td>
                    {run.verification_id ? (
                      <Button
                        variant="link"
                        size="sm"
                        className="d-flex align-items-center gap-1"
                        style={{ textDecoration: 'none', color: 'var(--primary)', padding: 0 }}
                        disabled={busyRunId === run.run_id}
                        onClick={() => handleShowVerification(run)}
                      >
                        <FileText size={14} /> Visa verifikat
                      </Button>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-end">
                    {run.run_id === latestPostedRunId && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="d-inline-flex align-items-center gap-1"
                        disabled={busyRunId === run.run_id}
                        onClick={() => handleUndo(run)}
                      >
                        <Undo2 size={14} /> Ångra
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {runModal}

      <VerificationDetailsModal
        showVerificationDetails={showVerificationDetails}
        setShowVerificationDetails={setShowVerificationDetails}
        selectedRow={verificationRow}
        viewOnly
      />

      {/* Muted tiny uppercase column labels — same treatment as the register. */}
      <style>{`
        .asset-run-table thead th {
          font-size: 0.6875rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
        }
        .asset-run-table tbody td { border-color: #f3f4f6; }
      `}</style>
    </>
  );
};

export default RunHistory;
